import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import type { Customer } from "@ippon/shared-types";
import { toApiErrorPayload, type ApiErrorPayload } from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

export type CreateCustomerBody = {
  customer_type: Customer["customer_type"];
  // 個人
  last_name?: string;
  first_name?: string;
  // 法人
  company_name?: string;
  representative_last_name?: string;
  representative_first_name?: string;
  // 連絡先
  phone?: string;
  email?: string;
};

export type UpdateCustomerBody = Partial<CreateCustomerBody> & {
  /**
   * カスケード波及から除外する契約 ID (制御パラメータ)。契約更新(renewal)経由で
   * マスタ更新する際、当該契約は renewal 側で「更新」処理済みなので二重付与を避ける。
   */
  exclude_contract_id?: string;
};

/** 顧客一覧取得 (keyword で氏名/連絡先 部分一致フィルタ) */
export const fetchCustomers = createAsyncThunk<
  Customer[],
  { keyword?: string } | undefined,
  { rejectValue: ApiErrorPayload }
>("customers/fetch", async (params, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/customers`, {
      params: params?.keyword ? { keyword: params.keyword } : undefined,
    });
    const data = res.data.data;
    return (Array.isArray(data) ? data : data?.items || []) as Customer[];
  } catch (error: unknown) {
    return rejectWithValue(toApiErrorPayload(error, "顧客一覧の取得に失敗しました"));
  }
});

export const getCustomer = createAsyncThunk<
  Customer,
  string,
  { rejectValue: ApiErrorPayload }
>("customers/get", async (customerId, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/customers/${customerId}`);
    return res.data.data as Customer;
  } catch (error: unknown) {
    return rejectWithValue(toApiErrorPayload(error, "顧客の取得に失敗しました"));
  }
});

export const createCustomer = createAsyncThunk<
  Customer,
  CreateCustomerBody,
  { rejectValue: ApiErrorPayload }
>("customers/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/customers`, body);
    return res.data.data as Customer;
  } catch (error: unknown) {
    return rejectWithValue(toApiErrorPayload(error, "顧客の登録に失敗しました"));
  }
});

export const updateCustomer = createAsyncThunk<
  Customer,
  { customer_id: string; body: UpdateCustomerBody },
  { rejectValue: ApiErrorPayload }
>("customers/update", async ({ customer_id, body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/customers/${customer_id}`,
      body,
    );
    return res.data.data as Customer;
  } catch (error: unknown) {
    return rejectWithValue(toApiErrorPayload(error, "顧客の更新に失敗しました"));
  }
});

export const deleteCustomer = createAsyncThunk<
  { customer_id: string },
  string,
  { rejectValue: ApiErrorPayload }
>("customers/delete", async (customerId, { rejectWithValue }) => {
  try {
    await ipponApi.delete(`${BaseURL}/api/v1/customers/${customerId}`);
    return { customer_id: customerId };
  } catch (error: unknown) {
    return rejectWithValue(toApiErrorPayload(error, "顧客の削除に失敗しました"));
  }
});

interface AsyncState<T> {
  data: T;
  loading: boolean;
  error: ApiErrorPayload | null;
}

interface CustomerState {
  list: AsyncState<Customer[]>;
  detail: AsyncState<Customer | null>;
}

const initialState: CustomerState = {
  list: { data: [], loading: false, error: null },
  detail: { data: null, loading: false, error: null },
};

export const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload ?? null;
      })
      .addCase(getCustomer.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(getCustomer.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload;
      })
      .addCase(getCustomer.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload ?? null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        // 一覧に即時反映 (重複は弾く)
        if (!state.list.data.some((c) => c.customer_id === action.payload.customer_id)) {
          state.list.data.push(action.payload);
        }
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (c) => c.customer_id === action.payload.customer_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
        if (state.detail.data?.customer_id === action.payload.customer_id) {
          state.detail.data = action.payload;
        }
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (c) => c.customer_id !== action.payload.customer_id,
        );
      });
  },
});

export default customerSlice.reducer;
